import Constants from '@/Constants';
import { Attachment } from '@/models/Attachment';
import { ExtensionTypeIcon } from '@/models/ExtensionTypeIcon';
import Item from '@/models/Item';
import { LanguageType } from '@/models/LanguageType';
import { ApplicationState } from '@/store';
import { attachmentsClear, getAttachmentListRequest, removeAttachmentRequest } from '@/store/attachments/action';
import { AttachmentRequest } from '@/store/attachments/types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ConfirmDialog from '../ConfirmDialog';
import RenameDropDown from '../RenameDropDown';
import './style.scss';

export enum AttachmentType {
    STUDENT = 'STUDENT',
    ADMIN = 'ADMIN',
}

interface Props {
    type?: AttachmentType;
    content: Item;
    locale?: LanguageType;
}

const Attachments: React.FC<Props> = (props: Props) => {
    const { type = AttachmentType.STUDENT, content, locale } = props;
    const dispatch = useDispatch();

    const { data: strings, currentContentLanguage } = useSelector((state: ApplicationState) => state.locale);
    const { data: attachments, success, loading } = useSelector((state: ApplicationState) => state.attachments);
    const [attachmentsArray, setAttachmentsArray] = useState<Attachment[]>([]);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [attachId, setAttachId] = useState<number>(0);
    const [attachName, setAttachName] = useState<string>('');

    const showEmptySection = !loading && attachmentsArray.length === 0;
    const isStudentView = type === AttachmentType.STUDENT;

    useEffect(() => {
        const currentLocale = locale || currentContentLanguage || strings._LANGUAGE;

        if (content.id) {
            dispatch(getAttachmentListRequest({ locale: currentLocale, entityId: content.id } as AttachmentRequest));
        }

        return () => {
            dispatch(attachmentsClear());
        };
    }, [currentContentLanguage, content]);

    useEffect(() => {
        attachments && setAttachmentsArray(attachmentsPathCorrection());
    }, [attachments]);

    useEffect(() => {
        if (success && !loading) {
            setShowConfirmDialog(false);
        }
    }, [success, loading]);

    const getIconByExtensionType = (filePath: string): ExtensionTypeIcon => {
        const extension: string = filePath.substr(filePath.lastIndexOf('.') + 1).toLowerCase();
        const types: ExtensionTypeIcon[] = [
            { ext: Constants.imageTypesList, icon: Constants.icons.IMAGE_OUTLINE },
            { ext: Constants.audioTypesList, icon: Constants.icons.AUDIOTRACK },
            { ext: Constants.videoTypesList, icon: Constants.icons.ONDEMAND_VIDEO },
            { ext: Constants.docsTypesList, icon: Constants.icons.FILE_DOCUMENT_OUTLINE },
            { ext: Constants.pdfTypeList, icon: Constants.icons.PDF },
        ];
        const genericType: ExtensionTypeIcon = { ext: [], icon: Constants.icons.INSERT_DRIVE_FILE };
        const result: ExtensionTypeIcon = types.find((t: ExtensionTypeIcon) => t.ext.indexOf(extension) !== -1) || genericType;
        return result;
    };

    const attachmentsPathCorrection = (): Attachment[] => {
        return attachments.map((item) => {
            if (item.path.startsWith('/static')) {
                if (process.env.NODE_ENV === 'development') {
                    item.path = window.location.origin.split(':3000')[0] + item.path;
                }
            }
            return item;
        });
    };

    const actionModalPrepareToShow = (attachId: number, attachName: string) => {
        setAttachId(attachId);
        setAttachName(formatText(attachName, 40));
        setShowConfirmDialog(Boolean(attachId > 0 && attachName));
    };

    const formatText = (text: string, size = 60) => (text.length > size ? `${text.substring(0, size)}...` : text);

    return (
        <div className="attachment-section" id={Constants.ATTACHMENTS_SECTION_ID}>
            {isStudentView && <h2>{strings.ATTACHMENTS}</h2>}

            {!showEmptySection && (
                <div className={'attachment-list'}>
                    <ConfirmDialog
                        content={`${strings.CONFIRM_DELETION_ATTACHMENT_MESSAGE} "${attachName}"?`}
                        icon={Constants.icons.REPORT_PROBLEM}
                        leftButtonTitle={strings.YES}
                        leftClick={() => dispatch(removeAttachmentRequest(attachId))}
                        onExited={() => null}
                        rightButtonTitle={strings.NO}
                        rightClick={() => setShowConfirmDialog(false)}
                        show={showConfirmDialog}
                        title={strings.ATTENTION}
                    />

                    {attachmentsArray.map((attachment: Attachment) => (
                        <div className={`attachment-item ${isStudentView ? '' : 'border-item'}`} key={attachment.id}>
                            <div className="d-flex align-items-center">
                                <i className="material-icons-outlined">{getIconByExtensionType(attachment.path).icon}</i>
                                <a
                                    className={`${isStudentView ? 'student-link' : ''}`}
                                    href={attachment.path}
                                    rel="noopener noreferrer"
                                    target="_blank"
                                    title={attachment.name}
                                >
                                    {formatText(attachment.name)}
                                </a>
                            </div>

                            {!isStudentView && (
                                <div className="attachment-actions">
                                    <RenameDropDown attachment={attachment} />

                                    <button className="btn btn-link" onClick={() => actionModalPrepareToShow(attachment.id, attachment.name)}>
                                        {strings.DELETE}
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {isStudentView && showEmptySection && <p className="empty-state">{strings.THERE_ARE_NO_ATTACHMENTS}</p>}
        </div>
    );
};
export default Attachments;
